if (!customElements.get('gift-builder')) {
  customElements.define(
    'gift-builder',
    class GiftBuilder extends HTMLElement {
      constructor() {
        super();
        this.rules = [];
        this.items = [];
        this.collectionLimits = new Map();
        this.currentRule = null;
        this.isSubmitting = false;
        this.fallbackGiftVolume = 1;
        this.fallbackGiftDate = '';
      }

      connectedCallback() {
        const rulesElement = this.querySelector('[data-box-rules]');

        try {
          this.rules = JSON.parse(rulesElement?.textContent || '[]');
        } catch (error) {
          this.showError('ボックス設定を読み込めませんでした。');
          return;
        }

        this.rules.sort((a, b) => Number(a.limit) - Number(b.limit));
        this.maxCapacity = Math.max(...this.rules.map((rule) => Number(rule.limit) || 0), 30);
        this.maxQuantity = 20;
        this.giftName = this.dataset.giftName || '選べる焼き菓子ギフト';

        this.applyHiddenProductKeywords();
        this.loadCollectionLimits();
        this.productCards = [...this.querySelectorAll('[data-gift-item]:not([hidden])')];
        this.noshiOptions = [...this.querySelectorAll('[data-noshi-option]')];
        this.submitButton = this.querySelector('[data-add-gift]');
        this.clearButton = this.querySelector('[data-clear-gift]');
        this.summaryTriggers = [...this.querySelectorAll('[data-summary-trigger]')];
        this.summaryPanel = this.querySelector('[data-gift-summary]');

        this.productCards.forEach((card) => this.bindCard(card));
        this.noshiOptions.forEach((option) => {
          option.addEventListener('change', () => this.updateNoshi());
        });
        this.submitButton?.addEventListener('click', () => this.addGiftToCart());
        this.clearButton?.addEventListener('click', () => this.clearGift());
        this.summaryTriggers.forEach((trigger) => {
          trigger.addEventListener('click', () => this.scrollToSummary());
        });
        this.updateNoshi();
        this.update();
      }

      loadCollectionLimits() {
        this.collectionLimits.clear();

        this.querySelectorAll('[data-collection-group]').forEach((group) => {
          const groupId = group.dataset.groupId;
          const limitsElement = group.querySelector('[data-group-box-limits]');
          if (!groupId || !limitsElement) return;

          try {
            const limits = JSON.parse(limitsElement.textContent || '{}');
            this.collectionLimits.set(groupId, limits);
          } catch (error) {
            this.collectionLimits.set(groupId, {});
          }
        });
      }

      applyHiddenProductKeywords() {
        this.querySelectorAll('[data-collection-group]').forEach((group) => {
          const keywordsElement = group.querySelector('[data-group-hidden-title-keywords]');
          if (!keywordsElement) return;

          let keywordText = '';

          try {
            keywordText = JSON.parse(keywordsElement.textContent || '""');
          } catch (error) {
            keywordText = '';
          }

          const keywords = String(keywordText)
            .split(/\r?\n/)
            .map((keyword) => keyword.trim().toLocaleLowerCase())
            .filter(Boolean);

          if (!keywords.length) return;

          group.querySelectorAll('[data-gift-item]').forEach((card) => {
            const title = (card.dataset.productTitle || '').toLocaleLowerCase();
            const shouldHide = keywords.some((keyword) => title.includes(keyword));

            if (shouldHide) {
              card.hidden = true;
              card.setAttribute('aria-hidden', 'true');
            }
          });
        });
      }

      scrollToSummary() {
        this.summaryPanel?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }

      bindCard(card) {
        const input = card.querySelector('[data-quantity-input]');
        const minus = card.querySelector('[data-quantity-minus]');
        const plus = card.querySelector('[data-quantity-plus]');
        const variantSelect = card.querySelector('[data-variant-select]');

        minus?.addEventListener('click', () => {
          input.value = Math.max(0, this.toInteger(input.value) - 1);
          this.update();
        });

        plus?.addEventListener('click', () => {
          const current = this.toInteger(input.value);
          const proposed = Math.min(this.maxQuantity, current + 1);
          const proposedItems = this.collectItems(new Map([[card, proposed]]));
          const proposedCapacity = this.getTotalCapacity(proposedItems);

          if (proposedCapacity > this.maxCapacity) {
            this.showError(`ギフトに入れられる上限は${this.maxCapacity}ポイントです。`);
            return;
          }

          if (!this.getRule(proposedCapacity, proposedItems)) {
            this.showError('コレクションごとの箱サイズ別上限を超えるため、追加できません。');
            return;
          }

          input.value = proposed;
          this.update();
        });

        input?.addEventListener('input', () => {
          let quantity = Math.min(this.maxQuantity, Math.max(0, this.toInteger(input.value)));
          input.value = quantity;
          let adjusted = false;

          while (quantity > 0) {
            const proposedItems = this.collectItems(new Map([[card, quantity]]));
            const proposedCapacity = this.getTotalCapacity(proposedItems);
            if (proposedCapacity <= this.maxCapacity && this.getRule(proposedCapacity, proposedItems)) break;
            quantity -= 1;
            adjusted = true;
          }

          input.value = quantity;
          if (adjusted) this.showError('容量またはコレクションごとの箱サイズ別上限に合わせて数量を調整しました。');

          this.update(false);
        });

        input?.addEventListener('change', () => this.update());

        variantSelect?.addEventListener('change', () => {
          const variant = this.getVariantData(card);
          const price = card.querySelector('[data-item-price]');
          if (price) price.textContent = this.formatMoney(variant.price);
          this.update();
        });
      }

      toInteger(value) {
        const parsed = Number.parseInt(value, 10);
        return Number.isFinite(parsed) ? parsed : 0;
      }

      getVariantData(card) {
        const field = card.querySelector('[data-variant-select]');

        if (!field) return { id: null, price: 0, available: false };

        if (field.tagName === 'SELECT') {
          const option = field.options[field.selectedIndex];
          return {
            id: Number(option?.value) || null,
            price: Number(option?.dataset.price) || 0,
            available: option?.dataset.available === 'true'
          };
        }

        return {
          id: Number(field.value) || null,
          price: Number(field.dataset.price) || 0,
          available: field.dataset.available === 'true'
        };
      }

      collectItems(quantityOverrides = null) {
        return this.productCards
          .map((card) => {
            const quantity = quantityOverrides?.has(card)
              ? this.toInteger(quantityOverrides.get(card))
              : this.toInteger(card.querySelector('[data-quantity-input]')?.value);
            const variant = this.getVariantData(card);

            return {
              card,
              title: card.dataset.productTitle || '',
              groupId: card.dataset.groupId || '',
              capacity: Number(card.dataset.capacity) || 0,
              quantity,
              variantId: variant.id,
              price: variant.price,
              available: variant.available
            };
          })
          .filter((item) => item.quantity > 0);
      }

      getTotalCapacity(items = this.collectItems()) {
        return items.reduce(
          (total, item) => total + item.capacity * item.quantity,
          0
        );
      }

      getRule(capacity, items = this.items) {
        if (capacity <= 0) return null;
        return this.rules.find((rule) => (
          capacity <= Number(rule.limit) && this.ruleSupportsItems(rule, items)
        )) || null;
      }

      ruleSupportsItems(rule, items) {
        const groupCounts = new Map();

        items.forEach((item) => {
          if (!item.groupId) return;
          groupCounts.set(item.groupId, (groupCounts.get(item.groupId) || 0) + item.quantity);
        });

        return [...groupCounts.entries()].every(([groupId, count]) => {
          const limits = this.collectionLimits.get(groupId);
          if (!limits || limits[rule.size] === undefined) return true;
          const configuredLimit = limits[rule.size];
          if (configuredLimit === null || configuredLimit === '') return true;
          const maximum = Math.max(0, Math.floor(Number(configuredLimit)));
          return !Number.isFinite(maximum) || count <= maximum;
        });
      }

      getNoshiValue() {
        return this.querySelector('[data-noshi-option]:checked')?.value || '熨斗なし';
      }

      updateNoshi() {
        this.setText('[data-noshi-value]', this.getNoshiValue());
      }

      update(clearError = true) {
        if (clearError) {
          this.hideError();
          this.hideStatus();
        }

        this.items = this.collectItems();
        const capacity = this.items.reduce(
          (total, item) => total + item.capacity * item.quantity,
          0
        );
        const productsTotal = this.items.reduce(
          (total, item) => total + item.price * item.quantity,
          0
        );

        this.currentRule = this.getRule(capacity);
        const boxPrice = Number(this.currentRule?.price) || 0;
        const limit = Number(this.currentRule?.limit) || 5;
        const remaining = Math.max(0, limit - capacity);

        this.setText('[data-current-capacity]', capacity);
        this.setText('[data-current-limit]', limit);
        this.setText('[data-products-total]', this.formatMoney(productsTotal));
        this.setText('[data-box-price]', this.formatMoney(boxPrice));
        this.setText('[data-grand-total]', this.formatMoney(productsTotal + boxPrice));
        this.setText('[data-floating-total]', this.formatMoney(productsTotal + boxPrice));
        this.setText(
          '[data-floating-box]',
          this.currentRule?.label || (
            capacity > this.maxCapacity
              ? '容量オーバー'
              : capacity > 0
                ? '対応ボックスなし'
                : '未選択'
          )
        );

        const boxName = this.querySelector('[data-box-name]');
        const capacityMessage = this.querySelector('[data-capacity-message]');
        const bar = this.querySelector('[data-capacity-bar]');

        if (this.currentRule) {
          boxName.textContent = `${this.currentRule.label} ギフトボックス`;
          capacityMessage.textContent = remaining > 0
            ? `あと${remaining}ポイント分追加できます`
            : `${this.currentRule.label}の容量いっぱいです`;
        } else {
          boxName.textContent = capacity > this.maxCapacity
            ? '容量オーバー'
            : capacity > 0
              ? '対応ボックスなし'
              : '未選択';
          capacityMessage.textContent = capacity > this.maxCapacity
            ? '商品の数量を減らしてください'
            : capacity > 0
              ? 'コレクションごとの箱サイズ別上限を確認してください'
              : '商品を選択してください';
        }

        if (bar) {
          const percentage = capacity > 0 ? Math.min(100, (capacity / limit) * 100) : 0;
          bar.style.width = `${percentage}%`;
        }

        this.updateBoxImage();
        this.updateButtons(capacity);
        this.updateSelectedItems();
      }

      updateBoxImage() {
        const placeholder = this.querySelector('[data-box-placeholder]');
        const images = [...this.querySelectorAll('[data-box-image]')];

        images.forEach((image) => {
          image.hidden = !this.currentRule || image.dataset.boxImage !== this.currentRule.size;
        });

        const activeImage = images.find((image) => !image.hidden);
        if (placeholder) placeholder.hidden = Boolean(activeImage);
      }

      updateSelectedItems() {
        const container = this.querySelector('[data-selected-items]');
        if (!container) return;

        container.replaceChildren();

        if (!this.items.length) {
          const empty = document.createElement('p');
          empty.className = 'gift-summary__none';
          empty.textContent = '選択中の商品はありません。';
          container.appendChild(empty);
          return;
        }

        const list = document.createElement('ul');
        list.className = 'gift-summary__list';

        this.items.forEach((item) => {
          const row = document.createElement('li');
          const details = document.createElement('div');
          const title = document.createElement('span');
          const price = document.createElement('span');
          const controls = document.createElement('div');
          const minus = document.createElement('button');
          const quantity = document.createElement('span');
          const plus = document.createElement('button');

          details.className = 'gift-summary__item-details';
          title.className = 'gift-summary__item-title';
          price.className = 'gift-summary__item-price';
          controls.className = 'gift-summary__item-quantity';
          minus.className = 'gift-summary__item-quantity-button';
          plus.className = 'gift-summary__item-quantity-button';
          quantity.className = 'gift-summary__item-quantity-value';

          title.textContent = item.title;
          price.textContent = this.formatMoney(item.price * item.quantity);
          minus.type = 'button';
          plus.type = 'button';
          minus.textContent = '−';
          plus.textContent = '＋';
          quantity.textContent = item.quantity;
          minus.setAttribute('aria-label', `${item.title}を1個減らす`);
          plus.setAttribute('aria-label', `${item.title}を1個増やす`);

          const sourceMinus = item.card.querySelector('[data-quantity-minus]');
          const sourcePlus = item.card.querySelector('[data-quantity-plus]');
          minus.disabled = !sourceMinus || sourceMinus.disabled;
          plus.disabled = !sourcePlus || sourcePlus.disabled;
          minus.addEventListener('click', () => sourceMinus?.click());
          plus.addEventListener('click', () => sourcePlus?.click());

          details.append(title, price);
          controls.append(minus, quantity, plus);
          row.append(details, controls);
          list.appendChild(row);
        });

        container.appendChild(list);
      }

      updateButtons(capacity) {
        this.productCards.forEach((card) => {
          const quantity = this.toInteger(card.querySelector('[data-quantity-input]')?.value);
          const plus = card.querySelector('[data-quantity-plus]');
          const minus = card.querySelector('[data-quantity-minus]');

          if (minus) minus.disabled = quantity <= 0 || card.classList.contains('gift-item--disabled');
          if (plus) {
            const proposedItems = this.collectItems(new Map([[card, quantity + 1]]));
            const proposedCapacity = this.getTotalCapacity(proposedItems);
            plus.disabled =
              card.classList.contains('gift-item--disabled') ||
              quantity >= this.maxQuantity ||
              proposedCapacity > this.maxCapacity ||
              !this.getRule(proposedCapacity, proposedItems);
          }
        });

        const hasUnavailableItem = this.items.some((item) => !item.available || !item.variantId);
        const boxReady = Boolean(
          this.currentRule &&
          this.currentRule.variantId &&
          this.currentRule.available
        );

        if (this.submitButton) {
          this.submitButton.disabled =
            this.isSubmitting ||
            !this.items.length ||
            capacity > this.maxCapacity ||
            hasUnavailableItem ||
            !boxReady;
        }

        if (this.clearButton) {
          this.clearButton.disabled = this.isSubmitting || !this.items.length;
        }

        if (this.currentRule && !this.currentRule.variantId) {
          this.showError(`${this.currentRule.label}の箱商品がテーマ設定で選択されていません。`);
        } else if (this.currentRule && !this.currentRule.available) {
          this.showError(`${this.currentRule.label}の箱が売り切れています。`);
        } else if (hasUnavailableItem) {
          this.showError('選択商品の一部が売り切れています。');
        }
      }

      clearGift(resetNoshi = false) {
        this.productCards.forEach((card) => {
          const input = card.querySelector('[data-quantity-input]');
          if (input) input.value = 0;
        });

        if (resetNoshi) {
          const noNoshi = this.querySelector('[data-noshi-option][value="熨斗なし"]');
          if (noNoshi) noNoshi.checked = true;
          this.updateNoshi();
        }

        this.update();
      }

      async addGiftToCart() {
        if (this.submitButton?.disabled || !this.currentRule || this.isSubmitting) return;

        this.isSubmitting = true;
        this.submitButton.disabled = true;
        const originalLabel = this.submitButton.textContent;
        this.submitButton.textContent = '追加しています…';
        this.hideError();
        this.hideStatus();

        const giftId = `gift-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const giftVolume = this.getGiftVolume();
        const giftLabel = `${this.giftName} vol.${giftVolume}`;
        const commonProperties = {
          '_ギフトID': giftId,
          '_ギフト種別': this.giftName,
          'ギフト名': giftLabel,
          'ボックスサイズ': this.currentRule.label
        };

        const items = this.items.map((item) => ({
          id: item.variantId,
          quantity: item.quantity,
          properties: {
            ...commonProperties,
            '_ギフト内商品': 'true'
          }
        }));

        items.push({
          id: Number(this.currentRule.variantId),
          quantity: 1,
          properties: {
            ...commonProperties,
            '_ギフトボックス': 'true',
            '合計容量': `${this.getTotalCapacity()}ポイント`,
            '熨斗': this.getNoshiValue()
          }
        });

        try {
          const response = await fetch(this.dataset.cartAddUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({ items })
          });

          if (!response.ok) {
            const result = await response.json().catch(() => ({}));
            throw new Error(result.description || result.message || 'カートへの追加に失敗しました。');
          }

          this.advanceGiftVolume(giftVolume);
          this.isSubmitting = false;
          this.submitButton.textContent = originalLabel;
          this.clearGift(true);
          this.showStatus(`${giftLabel}をカートに追加しました。続けて次のギフトを選べます。`);
        } catch (error) {
          this.showError(error.message || 'カートへの追加に失敗しました。時間をおいて再度お試しください。');
          this.isSubmitting = false;
          this.submitButton.textContent = originalLabel;
          this.update(false);
        }
      }

      getLocalDateKey() {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }

      getGiftVolumeStorageKey() {
        const shop = window.Shopify?.shop || window.location.hostname || 'shopify';
        return `gift-builder-volume:${shop}:${this.giftName}`;
      }

      getGiftVolume() {
        const today = this.getLocalDateKey();

        try {
          const saved = JSON.parse(localStorage.getItem(this.getGiftVolumeStorageKey()) || '{}');
          if (saved.date === today && Number(saved.next) >= 1) return Number(saved.next);
        } catch (error) {
          if (this.fallbackGiftDate === today) return this.fallbackGiftVolume;
        }

        this.fallbackGiftDate = today;
        this.fallbackGiftVolume = 1;
        return 1;
      }

      advanceGiftVolume(currentVolume) {
        const today = this.getLocalDateKey();
        const next = Number(currentVolume) + 1;
        this.fallbackGiftDate = today;
        this.fallbackGiftVolume = next;

        try {
          localStorage.setItem(
            this.getGiftVolumeStorageKey(),
            JSON.stringify({ date: today, next })
          );
        } catch (error) {
          // localStorageが利用できない場合は、ページを開いている間だけ連番を保持します。
        }
      }

      formatMoney(cents) {
        if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
          return window.Shopify.formatMoney(cents, this.dataset.moneyFormat);
        }

        return `${Math.round(Number(cents) / 100).toLocaleString('ja-JP')}円`;
      }

      setText(selector, value) {
        const element = this.querySelector(selector);
        if (element) element.textContent = value;
      }

      showError(message) {
        const element = this.querySelector('[data-error-message]');
        if (!element) return;
        element.textContent = message;
        element.hidden = false;
      }

      hideError() {
        const element = this.querySelector('[data-error-message]');
        if (!element) return;
        element.hidden = true;
        element.textContent = '';
      }

      showStatus(message) {
        const element = this.querySelector('[data-status-message]');
        if (!element) return;
        element.textContent = message;
        element.hidden = false;
      }

      hideStatus() {
        const element = this.querySelector('[data-status-message]');
        if (!element) return;
        element.hidden = true;
        element.textContent = '';
      }
    }
  );
}
